import { Container, Row, Col, Accordion } from "react-bootstrap";
import { PageTitleBlock } from "../components/layout/page-title-block";
import { useEffect, useMemo, useState } from "react";
import { IProduct } from "../models/api/IProduct";
import { IOrder } from "../models/api/IOrder";
import { useGetOrderHistoryQuery } from "../api/ecosternApi";
import { useAppSelector } from "../store/hooks";
import { selectCurrentClientId } from "../store/slices/userSlice";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { IPageableState } from "../models/content/pagination/IPageableState";
import { PageBottomPagination } from "../components/pagination/page-bottom-pagination";
import { getPageItems } from "../components/pagination/helpers";
import { LoadingWrapper } from "../components/layout/loading-wrapper";
import { OrderAccordionItem } from "../components/order/order-accordion-item";
import { searchItems } from "../utils/utils";
import { WithPrefix } from "../types/TWithPrefix";
import { CartzillaIcon, CartzillaIconName } from "../components/ui/icons/cartzilla-icon";
import { FormattedMessage } from "react-intl";
import { useSearch } from "hooks/use-search";
import { useAnalytics } from "hooks/use-analytics";
import { TableHeaderSortableButton } from "components/ui/table/table-header-sortable-button";
import { getOrderSum } from "utils/order.utils";

interface IOrdersViewState extends IPageableState {
    orders: IUIOrder[];
    sortOption: SortOption;
    sortDirection: SortDirection;
}

export interface IUIOrder extends IOrder {
    total: number;
}

type OrderRowSearchProps = keyof WithPrefix<IProduct, "rows.product.">;

enum SortOption {
    Default = "orders.sortOption.default",
    Date = "orders.sortOption.date",
    Total = "orders.sortOption.total",
}

type SortDirection = "asc" | "desc";

export const OrdersView = () => {
    const selectedClientId = useAppSelector(selectCurrentClientId);
    const { data: orderHistoryResult, isLoading: isOrdersLoading, isError: isOrdersError } = useGetOrderHistoryQuery(selectedClientId ?? skipToken);
    const { query } = useSearch();
    const searchOptions = {
        threshold: 0.2,
    };
    const searchProps: (keyof IOrder | OrderRowSearchProps)[] = ["id", "date", "rows.product.name"];
    const { track } = useAnalytics();
    const [state, setState] = useState<IOrdersViewState>({
        orders: [],
        sortOption: SortOption.Default,
        sortDirection: "desc",
        totalItems: 0,
        currentPage: 1,
        pageSize: 30,
    });

    const ordersWithTotal = useMemo(() => {
        return orderHistoryResult?.map((o) => ({ ...o, total: getOrderSum(o) }));
    }, [orderHistoryResult]);

    useEffect(() => {
        let mounted = true;

        if (ordersWithTotal && mounted) {
            let orders = searchItems<IUIOrder, OrderRowSearchProps>(searchProps, ordersWithTotal, query, searchOptions);
            orders = sortOrders(orders);

            setState({ ...state, orders, totalItems: orders.length, currentPage: 1 });
        }

        return () => {
            mounted = false;
        };
    }, [ordersWithTotal, query, state.sortOption, state.sortDirection]);

    useEffect(() => {
        if (!query) return;
        track("search", { props: { search_query: query } });
    }, [query]);

    const onPageClick = (pageNumber: number) => {
        setState({ ...state, currentPage: pageNumber });
    };

    const sortOrders = (orders: IUIOrder[]) => {
        const _orders = [...orders];
        const directionMultiplier = state.sortDirection === "asc" ? 1 : -1;

        switch (state.sortOption) {
            case SortOption.Date:
                return _orders.sort((a, b) => (new Date(a.date).getTime() - new Date(b.date).getTime()) * directionMultiplier);
            case SortOption.Total:
                return _orders.sort((a, b) => (a.total - b.total) * directionMultiplier);
            case SortOption.Default:
            default:
                return orders;
        }
    };

    const onSortOptionClick = (option: SortOption) => {
        setState({
            ...state,
            sortDirection: state.sortOption === option && state.sortDirection === "desc" ? "asc" : "desc",
            sortOption: state.sortDirection === "asc" ? SortOption.Default : option,
        });
    };

    const getSortOptionDirection = (option: SortOption) => {
        if (state.sortOption !== option) return undefined;
        return state.sortDirection;
    };

    return (
        <>
            <PageTitleBlock routeName={"orders"} />
            <LoadingWrapper isLoading={isOrdersLoading} isError={isOrdersError}>
                <Container className={"pb-5 mb-2 mt-4 mb-md-4"}>
                    <Row>
                        <Col as={"section"}>
                            <Row className={"mx-n2"}>
                                <Col>
                                    <Row className={"mb-3 align-items-center ps-3 fs-sm fs-md-normal"} style={{ paddingRight: "3.375rem" }}>
                                        <Col xs={3} md={2}>
                                            <FormattedMessage id={"orderContents.code"} />
                                        </Col>
                                        <Col xs={3} md={2}>
                                            <TableHeaderSortableButton
                                                direction={getSortOptionDirection(SortOption.Date)}
                                                onClick={() => onSortOptionClick(SortOption.Date)}
                                            >
                                                <CartzillaIcon icon={CartzillaIconName.Time} className={"me-2 d-md-none"} />
                                                <span className={"d-none d-md-inline"}>
                                                    <FormattedMessage id={"history.date"} />
                                                </span>
                                            </TableHeaderSortableButton>
                                        </Col>
                                        <Col md={4} className={"d-none d-md-flex"}></Col>
                                        <Col xs={2} md={1}>
                                            #
                                        </Col>
                                        <Col xs={4} md={2}>
                                            <TableHeaderSortableButton
                                                direction={getSortOptionDirection(SortOption.Total)}
                                                onClick={() => onSortOptionClick(SortOption.Total)}
                                            >
                                                <FormattedMessage id={"history.total"} />
                                            </TableHeaderSortableButton>
                                        </Col>
                                    </Row>

                                    <Accordion>
                                        {getPageItems(state.orders, state.currentPage, state.pageSize).map((o) => (
                                            <OrderAccordionItem order={o} key={o.id} />
                                        ))}
                                    </Accordion>
                                </Col>
                            </Row>
                            <hr className="my-3" />
                            <PageBottomPagination
                                totalItems={state.totalItems}
                                currentPage={state.currentPage}
                                pageSize={state.pageSize}
                                onPageClick={onPageClick}
                            />
                        </Col>
                    </Row>
                </Container>
            </LoadingWrapper>
        </>
    );
};
